import * as React from 'react';
import { OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import './bloodwork-ref-range-analysis.css';
import { ReferenceRangeChart } from '../charts/ReferenceRangeChart';
import { EGFRReferenceRangeChart } from '../charts/EGFRReferenceRangeChart';
import { Bloodwork, Section, PanelMarker, MenstrualPhase } from '../../models';
import { RDWReferenceRangeChart } from '../charts/RDWReferenceRangeChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type Props = {
  title: string;
  section: Section;
  bloodwork: Bloodwork;
  previousBloodwork?: Bloodwork;
  panelMarkers: PanelMarker[];
  infoLink: string;
  // PDF export rendering. The external-link ("learn more") icon next to each
  // marker name is interactive and only useful on screen — it never renders in
  // the exported PDF — so we omit it here, which also lets the marker name sit
  // flush-right with the value/unit beneath it.
  forPdf?: boolean;
};

type State = {
  showRefRangeModal: boolean;
};

export class ReferenceRangeAnalysis extends React.Component<Props, {}> {
  state: State = {
    showRefRangeModal: false,
  };

  showRefRangeModal = () => {
    const { infoLink } = this.props;
    if (infoLink) {
      window.open(infoLink);
    } else {
      this.setState({
        showRefRangeModal: true,
      });
    }
  };

  onHideRefRangeModal = () => {
    this.setState({
      showRefRangeModal: false,
    });
  };

  labCorpInfoTooltip = (panelMarker: PanelMarker) => {
    // tslint:disable-next-line:max-line-length
    const labCorpInfoTooltip =
      'Click this icon to learn more about how the reference ranges for this marker were determined.';
    return <Tooltip id="modal-tooltip">{labCorpInfoTooltip}</Tooltip>;
  };

  displayChart(panelMarker: PanelMarker, index: number) {
    console.log({ eGFR: panelMarker });
    let chart = (
      <ReferenceRangeChart
        panelMarker={panelMarker}
        chartContainerClass="reference-range-chart-container"
        showPlotBandLabels={index === 1}
      />
    );
    if (panelMarker.isEGFR() || panelMarker.field.useEGFRChart) {
      chart = (
        <EGFRReferenceRangeChart
          panelMarker={panelMarker}
          chartContainerClass="reference-range-chart-container"
        />
      );
    } else if (panelMarker.field.useRDWChart) {
      chart = (
        <RDWReferenceRangeChart
          panelMarker={panelMarker}
          chartContainerClass="reference-range-chart-container"
        />
      );
    }
    return chart;
  }

  openWindow(pm?: PanelMarker) {
    if (pm) {
      window.open(pm.field.labCorpLink);
    } else if (this.props.infoLink) {
      window.open(this.props.infoLink);
    }
  }

  showChart(index: number): string {
    return index === 0 ? 'none' : 'block';
  }

  // A "previous-only" marker is blank in the current test but carries a
  // previous-test value (see BloodworkAnalysis.getPreviousOnlyPanelMarkers).
  // Its chart shows just the previous (blue) blood drop, so the value/label
  // beneath it must come from the previous marker, not the (empty) current one.
  isPreviousOnly(pm: PanelMarker): boolean {
    return (
      !pm.hasValue() &&
      !!pm.previousPanelMarker &&
      pm.previousPanelMarker.hasValue()
    );
  }

  formatMenstrualPhase(phase: MenstrualPhase) {
    return phase === MenstrualPhase.NONE
      ? `${MenstrualPhase.NONE} (children's ranges)`
      : phase;
  }

  menstrualPhase() {
    const { section, bloodwork, previousBloodwork } = this.props;
    if (section !== Section.SEX_HORMONE) {
      return null;
    }
    const menstrualPhase = bloodwork.menstrualPhase;
    if (!menstrualPhase) {
      return null;
    }
    const previousMenstrualPhase = previousBloodwork && previousBloodwork.menstrualPhase;
    return (
      <div>
        <span style={{ marginLeft: '20px', fontWeight: 'bold' }}>
          Menstrual Phase
        </span>
        <span style={{ marginLeft: '8px' }}>
          {this.formatMenstrualPhase(menstrualPhase)}
        </span>
        {previousMenstrualPhase && (
          <>
            <span style={{ marginLeft: '24px', fontWeight: 'bold' }}>
              Menstrual Phase for Previous Collection Date:
            </span>
            <span style={{ marginLeft: '8px' }}>
              {this.formatMenstrualPhase(previousMenstrualPhase)}
            </span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="previous-menstrual-phase-tooltip">
                  <div>
                    The previous blood drop is plotted at its position within the
                    {' '}{this.formatMenstrualPhase(previousMenstrualPhase)} reference range.
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    Even though this chart shows the
                    {' '}{this.formatMenstrualPhase(menstrualPhase)} bands, judge the
                    previous value only by the
                    {' '}{this.formatMenstrualPhase(previousMenstrualPhase)} range.
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    For example, a drop at the low end of optimal is at the low end
                    of optimal for {this.formatMenstrualPhase(previousMenstrualPhase)},
                    not {this.formatMenstrualPhase(menstrualPhase)}.
                  </div>
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                icon="info-circle"
                style={{ marginLeft: '8px', color: 'var(--primary)', cursor: 'pointer' }}
              />
            </OverlayTrigger>
          </>
        )}
      </div>
    );
  }

  render() {
    const { panelMarkers } = this.props;
    if (!panelMarkers || panelMarkers.length === 0) {
      return null;
    }

    // This is a hack because the blood drop on the first blood drop is always misaligned.
    // We take the first marker and place it at the beginning of the array and then
    // we don't display it.
    const fakePanelMarker: PanelMarker = panelMarkers[0];
    panelMarkers.unshift(fakePanelMarker);

    // Whether any row is a "previous-only" marker — drives the footnote below
    // the chart that explains the asterisk next to those marker names.
    const hasPreviousOnly = panelMarkers.some(pm => this.isPreviousOnly(pm));

    return (
      <div>
        {this.menstrualPhase()}
        <Table className="labsmartz-chart-table">
          <tbody>
            {panelMarkers.map((pm: PanelMarker, index: number) => {
              return (
                <tr key={index} style={{ display: this.showChart(index) }}>
                  <td className="text-nowrap ref-range-category">
                    {this.isPreviousOnly(pm) && (
                      <span
                        className="ref-range-previous-only-asterisk"
                        title="Previous test only — see the note below the chart."
                      >
                        *
                      </span>
                    )}
                    {pm.field.getChartLabel()}
                    {!this.props.forPdf && (
                      <>
                        &nbsp;&nbsp;
                        {pm.field.labCorpLink ? (
                          <OverlayTrigger overlay={this.labCorpInfoTooltip(pm)}>
                            <FontAwesomeIcon
                              icon="external-link-alt"
                              onClick={() => {
                                this.openWindow(pm);
                              }}
                            />
                          </OverlayTrigger>
                        ) : null}
                      </>
                    )}
                    <br />
                    <span className="ref-range-category-value">
                      {this.isPreviousOnly(pm)
                        ? pm.previousPanelMarker!.displayValue()
                        : pm.displayValue()}{' '}
                      {pm.field.getChartUnits()}
                    </span>
                  </td>
                  <td>{this.displayChart(pm, index)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {hasPreviousOnly && (
          <div className="ref-range-previous-only-note">
            <span className="ref-range-previous-only-asterisk">*</span>
            {' '}Result from the previous test only — the current test has no
            value for this marker, so the chart shows just the previous (blue)
            blood drop.
          </div>
        )}
      </div>
    );
  }
}
